import { ComponentProps, FC } from "react";
import ShowViewButtonOverlay from "./ShowViewButtonOverlay";
import { cn } from "@src/lib/utils/cn";
import { PromptMediaFit } from "@src/types.generated";
import {
  fillCropStyle,
  isCroppableMediaType,
  ResolvedPromptMediaLayout,
} from "@src/features/libraryItem/shared/promptMediaLayout";
import { LinkType } from "@src/lib/utils/files";

type Props = {
  url: string;
  imageProps?: ComponentProps<"img">;
  showViewButton?: boolean;
  /** Null on legacy screens, which keep the historical contain-on-white look. */
  layout?: ResolvedPromptMediaLayout | null;
};

const PromptMediaImageAndGifFilePreview: FC<Props> = (props) => {
  const layout = props.layout;
  const isFill = layout?.fit === PromptMediaFit.Fill;
  return (
    <div
      className={cn(
        "relative flex self-stretch",
        // A managed box is sized by the author, so the image has to fill it.
        // In a flex column `self-stretch` only stretches width, which would
        // leave object-cover nothing to cover.
        layout && "h-full w-full overflow-hidden",
      )}
    >
      {layout && !isFill && (
        // Fit letterboxes; a blurred, over-scaled copy fills the bars. The
        // scale hides the transparent edge blur() leaves behind.
        <img
          alt=""
          aria-hidden
          data-testid="prompt-media-fit-backdrop"
          src={props.url}
          className="absolute inset-0 size-full scale-110 object-cover blur-2xl"
        />
      )}
      <img
        alt=""
        src={props.url}
        {...props.imageProps}
        className={cn(
          layout
            ? cn(
                "relative size-full max-w-full",
                isFill ? "object-cover" : "object-contain",
              )
            : "max-h-[auto] w-full max-w-full overflow-hidden bg-white object-contain object-center",
        )}
        style={{
          ...props.imageProps?.style,
          // Fill crops on screen: focal point plus zoom, never the file. Only
          // where the app can show the same crop (not Giphy, played as video).
          ...(layout &&
          isFill &&
          isCroppableMediaType(LinkType.IMAGE, props.url)
            ? fillCropStyle(layout)
            : undefined),
        }}
      />
      {/* After the image: a managed image is positioned (and a zoomed one
          is its own stacking context), so an earlier overlay would paint
          underneath it and the learner would lose "View". */}
      {props.showViewButton && <ShowViewButtonOverlay url={props.url} />}
    </div>
  );
};

export default PromptMediaImageAndGifFilePreview;
